/**
 * SSO callback handling (STORY-349).
 *
 * The backend no longer puts the session token in the post-login redirect.
 * It redirects to `/?sso_code=<one-time code>` (or `/?sso_error=<reason>`),
 * and the code is traded for the token with a POST — single use, ≤ 60 s.
 */

export interface SsoCallbackParams {
  code: string | null;
  error: string | null;
}

export function readSsoCallback(search: string): SsoCallbackParams {
  const params = new URLSearchParams(search);
  return { code: params.get('sso_code'), error: params.get('sso_error') };
}

// The code is single use, but React StrictMode runs the mount effect twice in
// development: share one exchange per code so the second run does not burn a
// second request and report a spurious failure.
const inflight = new Map<string, Promise<string>>();

export function exchangeSsoCodeOnce(
  code: string,
  exchange: (code: string) => Promise<{ access_token: string }>,
): Promise<string> {
  let pending = inflight.get(code);
  if (!pending) {
    pending = exchange(code).then((r) => r.access_token);
    inflight.set(code, pending);
  }
  return pending;
}

export type SsoErrorMessageKey = 'ssoAccountDisabled' | 'ssoEmailNotVerified' | 'ssoFailed';

export function ssoErrorMessageKey(error: string): SsoErrorMessageKey {
  if (error === 'account_disabled') return 'ssoAccountDisabled';
  if (error === 'email_not_verified') return 'ssoEmailNotVerified';
  return 'ssoFailed';
}
